import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { isCustomerDocumentPath } from './utils/documentAccess';

// Pages
import Dashboard from './pages/Dashboard';
import AdminUsers from './pages/AdminUsers';
import AdminBlogs from './pages/AdminBlogs';
import Settings from './pages/Settings';
import FontManager from './pages/FontManager';
import CustomerDocuments from './pages/CustomerDocuments';
import CustomerDocumentEditor from './pages/CustomerDocumentEditor';
import SigningPage from './pages/SigningPage';
import SSOCallback from './pages/SSOCallback';
import SessionExpired from './pages/SessionExpired';
import NotFound from './pages/NotFound';
import Landing from './pages/Landing';
import GuideViewer from './pages/GuideViewer';


// Layout
import AppLayout from './components/layout/AppLayout';

// Components
import SessionWarningModal from './components/SessionWarningModal';

const CanvasEditor = React.lazy(() => import('./pages/Editor'));
const DocumentEditorRouter = React.lazy(() => import('./pages/DocumentEditorRouter'));
const CreateDocumentFlow = React.lazy(() => import('./pages/CreateDocumentFlow'));
const WordEditor = React.lazy(() => import('./pages/WordEditor'));
const WordEditorSpike = React.lazy(() => import('./pages/WordEditorSpike'));

// Protected Route wrapper
function ProtectedRoute({ children, requireAdmin = false }) {
  const { user, loading, canAccessBuilder, customerDocsOnly } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user || !canAccessBuilder) {
    return <Navigate to="/auth/session-expired" state={{ from: location }} replace />;
  }

  if (customerDocsOnly && !isCustomerDocumentPath(location.pathname)) {
    return <Navigate to="/customer-documents" replace />;
  }

  if (requireAdmin && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const { loading, customerDocsOnly } = useAuth();
  const wordEditorSpikeEnabled = import.meta.env.VITE_WORD_EDITOR_SPIKE_ENABLED === 'true';

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/auth/sso" element={<SSOCallback />} />
      <Route path="/auth/session-expired" element={<SessionExpired />} />
      <Route path="/sign/:token" element={<SigningPage />} />
      <Route path="/landing" element={<Landing />} />
      <Route path="/guide/:guideId" element={<GuideViewer />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to={customerDocsOnly ? '/customer-documents' : '/dashboard'} replace />} />
        <Route path="dashboard" element={customerDocsOnly ? <Navigate to="/customer-documents" replace /> : <Dashboard />} />
        <Route path="editor/new" element={(
          <React.Suspense fallback={<div className="loading-screen"><div className="spinner" /><p>Loading...</p></div>}>
            <CreateDocumentFlow />
          </React.Suspense>
        )} />
        <Route
          path="editor/:id"
          element={(
            <React.Suspense fallback={<div className="loading-screen"><div className="spinner" /><p>Loading...</p></div>}>
              <DocumentEditorRouter CanvasEditor={CanvasEditor} WordEditor={WordEditor} />
            </React.Suspense>
          )}
        />
        <Route path="settings" element={<Settings />} />
        <Route
          path="settings/fonts"
          element={
            <ProtectedRoute requireAdmin>
              <FontManager />
            </ProtectedRoute>
          }
        />
        <Route path="customer-documents" element={<CustomerDocuments />} />
        <Route path="customer-document-editor" element={<CustomerDocumentEditor />} />
        <Route
          path="word-editor-spike"
          element={wordEditorSpikeEnabled ? (
            <React.Suspense fallback={<div className="loading-screen"><div className="spinner" /><p>Loading...</p></div>}>
              <WordEditorSpike />
            </React.Suspense>
          ) : <Navigate to="/dashboard" replace />}
        />

        {/* Admin routes */}
        <Route
          path="admin/users"
          element={
            <ProtectedRoute requireAdmin>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/blogs"
          element={
            <ProtectedRoute requireAdmin>
              <AdminBlogs />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <SessionWarningModal />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'var(--color-gray-800)',
                color: 'var(--color-white)',
              },
              success: {
                iconTheme: {
                  primary: 'var(--color-success)',
                  secondary: 'var(--color-white)',
                },
              },
              error: {
                iconTheme: {
                  primary: 'var(--color-error)',
                  secondary: 'var(--color-white)',
                },
              },
            }}
          />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
